import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Seo from '@/components/Seo';
import { IndianRupee, ShieldCheck, GraduationCap, Award, ArrowRight, CheckCircle2, Briefcase, Clock, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITIES = ['Mumbai', 'Pune', 'Bengaluru', 'Hyderabad', 'Delhi', 'Gurgaon', 'Noida', 'Chennai', 'Kolkata', 'Ahmedabad', 'Indore', 'Bhopal', 'Lucknow', 'Jaipur', 'Chandigarh', 'Coimbatore', 'Bhubaneswar', 'Surat', 'Vadodara', 'Nashik', 'Nagpur', 'Visakhapatnam', 'Kochi', 'Goa'];

const BecomeAProPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', city: 'Mumbai',
    category_slug: '', experience_years: 1, languages: '', bio: '',
  });

  useEffect(() => {
    fetch(`${API}/services/categories`).then(r => r.json()).then((d) => {
      setCategories(d.categories || []);
      setForm((p) => ({ ...p, category_slug: d.categories?.[0]?.slug || '' }));
    });
  }, []);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.phone || !form.email || !form.category_slug) {
      toast.error('Fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        experience_years: Number(form.experience_years) || 1,
        languages: form.languages ? form.languages.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      const res = await fetch(`${API}/services/providers/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      toast.success(data.message);
      navigate('/services');
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="become-a-pro-page">
      <Seo title="Become a Pro · Bharat Estate Services" description="Join Bharat Estate as a verified service professional. Steady jobs, doorstep customers, fair pay." path="/become-a-pro" />

      {/* HERO */}
      <section className="relative pt-36 pb-20 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxury relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/15 text-[#D4AF37] rounded-full text-sm font-medium mb-5 border border-[#D4AF37]/30">
              <Briefcase className="h-4 w-4" /> Earn with Bharat Estate
            </span>
            <h1 className="font-['Plus_Jakarta_Sans'] text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
              Be your own boss. <span className="text-[#D4AF37]">We bring the customers.</span>
            </h1>
            <p className="text-white/75 text-lg max-w-2xl mb-8">
              Join 8,000+ verified pros earning ₹25,000 – ₹80,000/month doing what they're great at — across 24+ cities.
            </p>
            <div className="grid grid-cols-2 gap-5 max-w-md">
              {[
                { k: '₹45K', l: 'Avg monthly earning' },
                { k: '8,000+', l: 'Active pros' },
                { k: '0', l: 'Joining fee' },
                { k: '7 days', l: 'Onboarding' },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#D4AF37]">{s.k}</p>
                  <p className="text-xs text-white/65 uppercase tracking-wider">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-sm p-6 md:p-8 text-[#0F172A] shadow-2xl" data-testid="pro-application-form">
            <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold mb-1">Apply in 60 seconds</h3>
            <p className="text-sm text-muted-foreground mb-5">No CV. No fee. Just answer 6 questions.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Full Name *</Label>
                <Input className="mt-1" value={form.name} onChange={(e) => upd('name', e.target.value)} data-testid="pro-name" />
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input className="mt-1" value={form.phone} onChange={(e) => upd('phone', e.target.value)} data-testid="pro-phone" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" className="mt-1" value={form.email} onChange={(e) => upd('email', e.target.value)} data-testid="pro-email" />
              </div>
              <div>
                <Label className="text-xs">City *</Label>
                <select className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" value={form.city} onChange={(e) => upd('city', e.target.value)} data-testid="pro-city">
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Skill / Category *</Label>
                <select className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" value={form.category_slug} onChange={(e) => upd('category_slug', e.target.value)} data-testid="pro-category">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Years of Experience *</Label>
                <Input type="number" min={0} max={50} className="mt-1" value={form.experience_years} onChange={(e) => upd('experience_years', e.target.value)} data-testid="pro-exp" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Languages you speak (comma separated)</Label>
                <Input className="mt-1" value={form.languages} onChange={(e) => upd('languages', e.target.value)} placeholder="Hindi, English, Marathi" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Tell us about yourself</Label>
                <textarea rows={3} className="mt-1 w-full rounded-sm border border-input bg-background px-3 py-2 text-sm" value={form.bio} onChange={(e) => upd('bio', e.target.value)} placeholder="A line or two about your work, certifications, past employers..." />
              </div>
            </div>
            <Button onClick={submit} disabled={submitting} className="w-full mt-5 bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold py-6" data-testid="pro-submit-btn">
              {submitting ? 'Submitting...' : 'Apply Now'} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <p className="text-xs text-muted-foreground mt-3 text-center">By applying, you agree to our verification process.</p>
          </div>
        </div>
      </section>

      {/* WHY JOIN */}
      <section className="py-20">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Why Bharat Estate</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">Better customers. Better pay. Less hassle.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: IndianRupee, h: 'Steady weekly payouts', b: 'Get paid every Friday. No haggling, no chasing customers for cash.' },
              { i: Smartphone, h: 'Jobs in your phone', b: 'Pro app shows daily jobs, navigation, customer details — all in one tap.' },
              { i: GraduationCap, h: 'Free training & gear', b: 'Bharat Estate pays for SOP training, uniform, tool subsidy.' },
              { i: ShieldCheck, h: 'Insurance & support', b: 'On-job accident cover + 24/7 helpline for difficult customers.' },
            ].map((p, i) => (
              <div key={i} className="bg-white p-6 rounded-sm border border-border/50">
                <div className="h-11 w-11 rounded-sm bg-[#0F172A] text-[#D4AF37] flex items-center justify-center mb-4"><p.i className="h-5 w-5" /></div>
                <h4 className="font-semibold text-[#0F172A] mb-1">{p.h}</h4>
                <p className="text-sm text-muted-foreground">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 bg-white">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">7-day onboarding</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">From application to first job, fast.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { d: 'Day 1', h: 'Apply', b: 'Fill the 6-question form. We screen within 24 hrs.' },
              { d: 'Day 2-3', h: 'Verify', b: 'Aadhaar + police verification. PCC if needed.' },
              { d: 'Day 4-5', h: 'Train', b: '2-day SOP training (free). Uniform handed over.' },
              { d: 'Day 6', h: 'Trial Job', b: 'One paid trial job + supervisor feedback.' },
              { d: 'Day 7', h: 'Live', b: 'Receive jobs on the Pro app. Earn from day 7.' },
            ].map((s, i) => (
              <div key={i} className="bg-[#F9F9F7] p-5 rounded-sm">
                <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold mb-2">{s.d}</p>
                <p className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-2">{s.h}</p>
                <p className="text-xs text-muted-foreground">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-[#F9F9F7]">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Real pros, real stories</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">"I went from ₹18K freelance to ₹62K monthly."</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { n: 'Suresh, AC Pro', c: 'Mumbai · 4 years on platform', q: 'Earlier I worked under a contractor — half the money disappeared. Joined Bharat Estate, started earning ₹50K+ from month 2.' },
              { n: 'Anita, Beauty Pro', c: 'Delhi · 2 years on platform', q: 'I can work after dropping kids at school. Last month I did 84 jobs and earned ₹48,000. Better than my old parlour job.' },
              { n: 'Karthik, Carpenter', c: 'Pune · 3 years on platform', q: 'Customers respect us more. Tip ₹100-200 most jobs. The app schedules me, I just show up & finish well.' },
            ].map((t, i) => (
              <div key={i} className="bg-white p-6 rounded-sm">
                <Award className="h-7 w-7 text-[#D4AF37] mb-3" />
                <p className="text-[#0F172A] leading-relaxed mb-4 italic">"{t.q}"</p>
                <p className="font-semibold text-[#0F172A] text-sm">{t.n}</p>
                <p className="text-xs text-muted-foreground">{t.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-[#D4AF37]">
        <div className="container-luxury flex flex-col md:flex-row items-center justify-between gap-6 text-[#0F172A]">
          <div>
            <p className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold leading-tight">Ready to start? It's free.</p>
            <p className="text-sm mt-1 opacity-80">Scroll up and apply. We'll do the rest.</p>
          </div>
          <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-7 py-6 font-semibold">
            Apply Now <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BecomeAProPage;
